import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatAccordion } from '@angular/material/expansion';
import { NotificationTextMessage } from '@app/core/enums';
import { CommonService } from '@app/core/services';
import { GetEmailTemplate, SaveEmailTemplate } from '@app/core/store/settings';
import {
  AngularEditorComponent,
  AngularEditorConfig,
} from '@kolkov/angular-editor';
import { Store } from '@ngxs/store';

interface EmailTemplateTag {
  label: string;
  token: string;
}

interface EmailTemplateTagGroup {
  label: string;
  expanded: boolean;
  tags: EmailTemplateTag[];
}

@Component({
  selector: 'app-email-templates',
  templateUrl: './email-templates.component.html',
  styleUrls: ['./email-templates.component.scss'],
})
export class EmailTemplatesComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  isExpandAll = false;
  @ViewChild(MatAccordion) accordion: MatAccordion;
  @ViewChild(AngularEditorComponent) messageEditor?: AngularEditorComponent;
  private savedEditorRange: Range | null = null;
  private removeEditorListeners: Array<() => void> = [];

  readonly tagGroups: EmailTemplateTagGroup[] = [
    {
      label: 'Company information',
      expanded: true,
      tags: [
        { label: 'Company name', token: '[company_name]' },
        { label: 'Company UTR', token: '[company_utr]' },
        { label: 'Practice name', token: '[practice_name]' },
      ],
    },
    {
      label: 'Tax Return',
      expanded: true,
      tags: [
        { label: 'Tax year', token: '[tax_year]' },
        { label: 'Period from/start date', token: '[period_from]' },
        { label: 'Period to/end date', token: '[period_to]' },
      ],
    },
  ];

  toggleAccordion(): void {
    this.isExpandAll = !this.isExpandAll;
  }

  editorConfig: AngularEditorConfig = {
    editable: true,
    spellcheck: true,
    height: '15rem',
    minHeight: '5rem',
    placeholder: 'Enter text here...',
    translate: 'no',
    showToolbar: true,
    defaultParagraphSeparator: 'p',
    defaultFontName: 'Arial',
    toolbarHiddenButtons: [['bold']],
    customClasses: [
      {
        name: 'quote',
        class: 'quote',
      },
      {
        name: 'redText',
        class: 'redText',
      },
      {
        name: 'titleText',
        class: 'titleText',
        tag: 'h1',
      },
    ],
  };

  emailForm: FormGroup;

  constructor(
    private store: Store,
    private commonService: CommonService,
    private fb: FormBuilder
  ) {}
  ngOnInit(): void {
    this.setForm();
    this.getEmailTemplateData();
  }

  ngAfterViewInit(): void {
    this.registerEditorSelectionTracking();
  }

  ngOnDestroy(): void {
    this.removeEditorListeners.forEach((removeListener) => removeListener());
    this.removeEditorListeners = [];
  }

  setForm() {
    this.emailForm = this.fb.group({
      subject: ['', Validators.required],
      messageBody: ['', Validators.required],
    });
  }

  getEmailTemplateData() {
    this.store.dispatch(new GetEmailTemplate()).subscribe((res) => {
      this.savedEditorRange = null;
      this.emailForm.patchValue({
        subject: res.setting.emailData.subject,
        messageBody: res.setting.emailData.messageBody,
      });
    });
  }

  saveEmailTemplate() {
    const emailData = {
      subject: this.emailForm.controls.subject.value,
      emailBody: this.emailForm.controls.messageBody.value,
    };

    this.store.dispatch(new SaveEmailTemplate(emailData)).subscribe((res) => {
      if (res.setting.isSucess) {
        this.commonService.onSucess('Email template saved successfully');
      } else {
        this.commonService.onFailure(NotificationTextMessage.errorMessage);
      }
    });
  }

  resetForm() {
    this.getEmailTemplateData();
  }

  toggleTagGroup(group: EmailTemplateTagGroup): void {
    group.expanded = !group.expanded;
  }

  preserveEditorSelection(event: MouseEvent): void {
    event.preventDefault();
    this.captureEditorSelection();
  }

  insertTag(token: string): void {
    if (!this.messageEditor) {
      this.appendTagToMessageBody(token);
      return;
    }

    try {
      const inserted = this.insertTagAtCaret(token);

      if (!inserted) {
        this.appendTagToMessageBody(token);
        return;
      }

      this.syncMessageBodyFromEditor();
    } catch (error) {
      this.appendTagToMessageBody(token);
    }
  }

  private registerEditorSelectionTracking(): void {
    const editorElement = this.messageEditor?.textArea?.nativeElement as
      | HTMLElement
      | undefined;

    if (!editorElement) {
      return;
    }

    const selectionEvents: Array<keyof HTMLElementEventMap> = [
      'mouseup',
      'keyup',
      'input',
      'focus',
    ];

    selectionEvents.forEach((eventName) => {
      const handler = () => this.captureEditorSelection();
      editorElement.addEventListener(eventName, handler);
      this.removeEditorListeners.push(() =>
        editorElement.removeEventListener(eventName, handler)
      );
    });
  }

  private captureEditorSelection(): void {
    const editorElement = this.messageEditor?.textArea?.nativeElement as
      | HTMLElement
      | undefined;
    const selection = window.getSelection();

    if (!editorElement || !selection || selection.rangeCount === 0) {
      return;
    }

    const range = selection.getRangeAt(0);

    if (
      editorElement.contains(range.startContainer) &&
      editorElement.contains(range.endContainer)
    ) {
      this.savedEditorRange = range.cloneRange();
    }
  }

  private restoreEditorSelection(): void {
    const selection = window.getSelection();

    this.messageEditor?.focus();

    if (!selection || !this.savedEditorRange) {
      return;
    }

    selection.removeAllRanges();
    selection.addRange(this.savedEditorRange);
  }

  private insertTagAtCaret(token: string): boolean {
    const editorElement = this.messageEditor?.textArea?.nativeElement as
      | HTMLElement
      | undefined;
    const selection = window.getSelection();

    this.restoreEditorSelection();

    if (!editorElement || !selection) {
      return false;
    }

    const activeRange =
      selection.rangeCount > 0
        ? selection.getRangeAt(0).cloneRange()
        : this.savedEditorRange?.cloneRange() ?? null;

    if (
      !activeRange ||
      !editorElement.contains(activeRange.startContainer) ||
      !editorElement.contains(activeRange.endContainer)
    ) {
      return false;
    }

    activeRange.deleteContents();

    const tagNode = document.createTextNode(token);
    activeRange.insertNode(tagNode);

    const caretRange = document.createRange();
    caretRange.setStartAfter(tagNode);
    caretRange.collapse(true);

    selection.removeAllRanges();
    selection.addRange(caretRange);

    this.savedEditorRange = caretRange.cloneRange();
    editorElement.focus();

    return true;
  }

  private syncMessageBodyFromEditor(): void {
    const editorElement = this.messageEditor?.textArea?.nativeElement as
      | HTMLElement
      | undefined;

    if (editorElement) {
      this.messageEditor?.onContentChange(editorElement);
      this.captureEditorSelection();
    }
  }

  private appendTagToMessageBody(token: string): void {
    const currentValue = this.emailForm.controls.messageBody.value || '';
    const spacer = currentValue && !currentValue.endsWith(' ') ? ' ' : '';

    this.emailForm.controls.messageBody.setValue(`${currentValue}${spacer}${token}`);
  }

  closeClick() {
    history.back();
  }
}
